import classNames from "classnames";
import { useEffect, useState } from "react";
import i18n from "../../../../i18n";
import BFInput from "../../../../modules/abstract-ui/forms/input/BFInput";
import BFButton from "../../../../modules/abstract-ui/general/Button/BFButton";
import BFDropdown from "../../../../modules/abstract-ui/general/Dropdown/BFDropdown";
import { useTypedSelector } from "../../../../redux/hooks";
import {
  getAvailableLanguages,
  getMandatorDefaultLanguage,
} from "../../../../redux/reducers/ui-config/UiConfig";
import DataBusDefaults from "../../../../services/DataBusDefaults";
import LanguageService, {
  TranslatedLabel,
  TranslatedLabelObject,
} from "../../../../services/LanguageService";
import ValidationPopover from "../../general/ValidationPopover/ValidationPopover";
import { DefaultIcons } from "../../icon/DefaultIcons";
import LabeledInput from "../LabeledInput";
import "./BFDisplayNameInput.scss";

type RenderInputParams = {
  value: string;
  prefixText?: string;
  key?: string;
  mainValue?: string;
};
interface DisplaynameInputProps {
  value: TranslatedLabel;
  label: string;
  onChange: (value: TranslatedLabel) => void;
  error?: string;
  className?: string;
}

const BFDisplaynameInput = (props: DisplaynameInputProps) => {
  const allAvailableLanguages = useTypedSelector((state) =>
    getAvailableLanguages(state)
  );
  const mandatorDefaultLanguage = useTypedSelector((state) =>
    getMandatorDefaultLanguage(state)
  );
  const isTypeString =
    typeof props.value === "string" ||
    props.value === null ||
    props.value === undefined;

  // Addable languages that are not already in the value
  const [selectableLanguages, setSelectableLanguages] = useState<
    { selector: string; flag: string; name: string }[]
  >([]);

  const selectedLangsDependencyArray = !isTypeString
    ? Object.keys(props.value).join("_")
    : "";
  useEffect(() => {
    const selectedLangs: string[] = [];

    if (isTypeString) {
      selectedLangs.push(mandatorDefaultLanguage);
    } else {
      selectedLangs.push(...Object.keys(props.value));
    }

    const selectableLanguages = allAvailableLanguages.filter(
      (lang) => !selectedLangs.includes(lang.selector)
    );
    setSelectableLanguages(selectableLanguages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLangsDependencyArray]);

  const getLanguageName = (languageCode: string) => {
    const language = allAvailableLanguages.find(
      (language) => language.selector === languageCode
    );
    return language ? language.name : languageCode;
  };

  // Small hack to readjust the error popover position
  const [hideErrorPopover, setHideErrorPopover] = useState(false);
  useEffect(() => {
    if (props.error) {
      setHideErrorPopover(true);
    }
    const timer = setTimeout(() => {
      setHideErrorPopover(false);
    }, 15);
    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableLanguages.length]);
  // End of hack

  const onChangeValue = (value: string, key?: string) => {
    if (isTypeString) {
      props.onChange(value);
    }

    if (key) {
      const oldValues = props.value as TranslatedLabelObject;
      const newTranslatedLabel = {
        ...oldValues,
        [key]: value,
      };
      props.onChange(newTranslatedLabel);
    }
  };

  const renderLanguageSelectorComponent = () => {
    if (selectableLanguages.length === 0) {
      return null;
    }

    return (
      <div className="display-name-action">
        <BFDropdown
          placement="bottomEnd"
          items={selectableLanguages.map((lang) => ({
            identifier: lang.selector,
            type: "button",
            text: lang.name,
            onSelect: () => {
              const newObject: TranslatedLabelObject = {};
              if (typeof props.value === "string") {
                newObject[mandatorDefaultLanguage] = props.value as string;
              } else {
                const oldValues = props.value as TranslatedLabelObject;
                Object.keys(oldValues).forEach((key) => {
                  newObject[key] = oldValues[key];
                });
              }
              newObject[lang.selector] = "";
              props.onChange(newObject);
            },
          }))}
          label=""
          renderToggle={(props) => (
            <BFButton
              noPadding
              size="xs"
              tooltip={{
                tooltip: i18n.t(
                  "Global.DisplayName.AddTranslation",
                  "Übersetzung hinzufügen"
                ),
              }}
              icon={{ type: "light", size: "xs", data: "earth-add" }}
              appearance="link"
              {...props}
            />
          )}
        />
      </div>
    );
  };

  const renderInput = (params: RenderInputParams) => {
    let isAddedLanguage = false;
    if (params.key && params.key !== mandatorDefaultLanguage) {
      isAddedLanguage = true;
    }

    let renderLanguageSelector = false;
    if (!params.key || params.key === mandatorDefaultLanguage) {
      renderLanguageSelector = true;
    }

    return (
      <div className={classNames("display-name-input", props.className)}>
        <BFInput
          key={params.key}
          value={params.value}
          prefix={params.prefixText}
          onChange={(value) => {
            onChangeValue(value as string, params.key);
          }}
          removeSuffixPadding
          suffix={
            isAddedLanguage ? (
              <div className={`extra-actions`}>
                <BFButton
                  className={`suggest-translations`}
                  noPadding
                  disabled={(params.mainValue || "").trim().length === 0}
                  size="xs"
                  tooltip={{
                    tooltip: i18n.t(
                      "Global.DisplayName.SuggestTranslation",
                      "Übersetzung vorschlagen"
                    ),
                  }}
                  icon={{ size: "xs", type: "light", data: "translate" }}
                  appearance="link"
                  onClick={async () => {
                    const result = await LanguageService.suggestTranslation(
                      params.mainValue,
                      params.key
                    );
                    if (result) {
                      onChangeValue(result, params.key);
                    } else {
                      DataBusDefaults.toast({
                        type: "warning",
                        text: i18n.t(
                          "Global.DisplayName.SuggestTranslation.NoTranslation",
                          "Keine Übersetzung gefunden"
                        ),
                      });
                    }
                  }}
                />

                <BFButton
                  className={`remove-translation-button`}
                  noPadding
                  size="xs"
                  tooltip={{
                    tooltip: i18n.t(
                      "Global.DisplayName.DeleteTranslation",
                      "Übersetzung entfernen"
                    ),
                  }}
                  icon={{ size: "xs", ...DefaultIcons.TRASH }}
                  appearance="link"
                  onClick={() => {
                    const newValue = props.value as TranslatedLabelObject;
                    delete newValue[params.key as string];
                    props.onChange({ ...newValue });
                  }}
                />
              </div>
            ) : renderLanguageSelector ? (
              renderLanguageSelectorComponent()
            ) : undefined
          }
        />
      </div>
    );
  };

  const renderStringInput = () => {
    const params: RenderInputParams = {
      value: props.value as string,
      // prefixText: getLanguageName(mandatorDefaultLanguage),
    };
    return renderInput(params);
  };

  const renderObjectInput = () => {
    return (
      <>
        {Object.keys(props.value).map((languageCode) => {
          const languageName = getLanguageName(languageCode);
          const params: RenderInputParams = {
            value: (props.value as TranslatedLabelObject)[languageCode],
            prefixText:
              Object.keys(props.value).length > 1 ? languageName : undefined,
            key: languageCode,
            mainValue: props.value?.[mandatorDefaultLanguage],
          };
          return renderInput(params);
        })}
      </>
    );
  };

  const hasError = !!props.error;

  return (
    <div className={classNames("display-name-input")}>
      <LabeledInput label={props.label} labelPosition={"top"} error={hasError}>
        <ValidationPopover
          level={"error"}
          message={hideErrorPopover ? "" : props.error}
          marginTop={0}
        >
          <div
            className={classNames("display-name-input-group", {
              error: hasError,
            })}
          >
            {isTypeString ? renderStringInput() : renderObjectInput()}
          </div>
        </ValidationPopover>
      </LabeledInput>
    </div>
  );
};

export default BFDisplaynameInput;
